console.log('[UBL] uniform-bundle-limit.js loaded');

const PROP_KEY = '_uniform_bundle_limit';
const MESSAGE =
  'Only one uniform package can be added per order. ' +
  'Please check out with your current package before ordering another.';

async function getCart() {
  return fetch('/cart.js', { credentials: 'same-origin' }).then(r => r.json());
}

async function cartHasLimitedBundle() {
  const cart = await getCart();
  return cart.items.some(item => item.properties && item.properties[PROP_KEY] === '1');
}

function blockWithMessage() {
  alert(MESSAGE);
}

/**
 * Detect whether the outgoing request is trying to add a limited bundle.
 * Handles:
 *  - URL-encoded bodies
 *  - FormData
 *  - JSON bodies
 */
function requestAddsLimitedBundle(body) {
  try {
    if (!body) return false;

    // URL-encoded string
    if (typeof body === 'string') {
      return (
        body.includes(`properties%5B${encodeURIComponent(PROP_KEY)}%5D=1`) ||
        body.includes(`properties[${PROP_KEY}]=1`) ||
        body.includes(`${encodeURIComponent(`properties[${PROP_KEY}]`)}=1`)
      );
    }

    // URLSearchParams
    if (body instanceof URLSearchParams) {
      return body.get(`properties[${PROP_KEY}]`) === '1';
    }

    // FormData
    if (body instanceof FormData) {
      return body.get(`properties[${PROP_KEY}]`) === '1';
    }

    // JSON object (some themes send JSON)
    if (typeof body === 'object') {
      // If options.body is already an object, or if it was JSON-parsed upstream
      if (body.properties && body.properties[PROP_KEY] === '1') return true;

      // If it's something like { items: [{ properties: {...}}]}
      if (Array.isArray(body.items)) {
        return body.items.some(it => it?.properties?.[PROP_KEY] === '1');
      }
    }
  } catch (e) {
  }
  return false;
}

function makeBlockedResponse() {
  // Return a response shape that won’t crash a theme expecting JSON
  return new Response(JSON.stringify({ status: 422, message: MESSAGE }), {
    status: 422,
    headers: { 'Content-Type': 'application/json' }
  });
}

/**
 * 1) Intercept fetch() calls to /cart/add(.js) and /cart/change(.js)
 */
(function interceptFetch() {
  const originalFetch = window.fetch;

  window.fetch = async function(resource, options = {}) {
    const url =
      typeof resource === 'string'
        ? resource
        : (resource && resource.url) || '';

    const isAdd = url.includes('/cart/add');
    const isChange = url.includes('/cart/change');

    if (!isAdd && !isChange) {
      return originalFetch.apply(this, arguments);
    }

    // Only enforce on adds of limited bundles (and optionally quantity changes later)
    if (isAdd) {
      let body = options.body;

      // If theme sends JSON as a string, try to parse it
      let jsonBody = null;
      if (typeof body === 'string') {
        const trimmed = body.trim();
        if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
          try { jsonBody = JSON.parse(trimmed); } catch (_) {}
        }
      }

      const addingLimited = requestAddsLimitedBundle(jsonBody || body);

      if (addingLimited) {
        try {
          if (await cartHasLimitedBundle()) {
            blockWithMessage();
            return makeBlockedResponse();
          }
        } catch (err) {
          console.warn('[UBL] Cart check failed; allowing add-to-cart.', err);
        }
      }
    }

    return originalFetch.apply(this, arguments);
  };
})();

/**
 * 2) Intercept XMLHttpRequest (themes that don’t use fetch)
 */
(function interceptXHR() {
  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function(method, url) {
    this.__ubl_url = url;
    return originalOpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = async function(body) {
    const url = this.__ubl_url || '';
    const isAdd = typeof url === 'string' && url.includes('/cart/add');

    if (!isAdd) {
      return originalSend.apply(this, arguments);
    }

    // Attempt to detect limited bundle in the outgoing body
    let addingLimited = false;

    // body may be string, FormData, etc.
    addingLimited = requestAddsLimitedBundle(body);

    if (addingLimited) {
      try {
        if (await cartHasLimitedBundle()) {
          blockWithMessage();

          // Abort the request so it never reaches Shopify
          this.abort();
          return;
        }
      } catch (err) {
        console.warn('[UBL] Cart check failed; allowing add-to-cart.', err);
      }
    }

    return originalSend.apply(this, arguments);
  };
})();
